<template>
  <div class="signup-page">
    <div class="signup-container">
      <!-- Logo & Branding -->
      <div class="brand-section">
        <div class="logo-wrapper">
          <img src="@/assets/logo.svg" alt="לוגו הדרך אל הלב" class="logo" />
        </div>
        <h1 class="app-name">הדרך אל הלב</h1>
        <p class="app-tagline">יצירת חשבון חדש</p>
      </div>

      <!-- Loading Overlay -->
      <div v-if="flag" class="loading-overlay">
        <div class="loading-spinner">
          <svg class="spinner" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" fill="none" stroke-linecap="round" stroke-dasharray="31.4 31.4" />
          </svg>
          <span>יוצר חשבון...</span>
        </div>
      </div>

      <!-- Signup Card -->
      <div class="signup-card">
        <!-- Step Indicator -->
        <div class="step-indicator">
          <div :class="['step', { active: !policyAuth, completed: policyAuth }]">
            <span class="step-number">1</span>
            <span class="step-label">תקנון</span>
          </div>
          <div class="step-line" :class="{ completed: policyAuth }"></div>
          <div :class="['step', { active: policyAuth }]">
            <span class="step-number">2</span>
            <span class="step-label">פרטים אישיים</span>
          </div>
        </div>

        <Policy v-if="!policyAuth" @policy-authorized="onPolicyAuthorized" />
        <CreateUserInfo v-else @personal-details-submitted="onPersonalDetailsSubmitted" />
      </div>

      <!-- Footer -->
      <p class="footer-text">כבר יש לך חשבון? <router-link to="/login" class="login-link">התחבר</router-link></p>
    </div>
  </div>
</template>

<script setup>
import useSignup from '@/composables/useSignup'
import useCollection from '@/composables/useCollection'
import { ref } from 'vue'
import Policy from '../Policy.vue'
import CreateUserInfo from '../usersInfo/CreateUserInfo.vue'
import { projectAuth } from '@/firebase/config'
import { useRouter } from "vue-router"
import { states } from '../../main_store'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { showModal } from '@/main_store/modal'
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth'

const functions = getFunctions();
const updateLogin = httpsCallable(functions, 'update_login');
const create_user_with_phone_email = httpsCallable(functions, 'create_user_with_phone_email');

const flag = ref(false)
const auth = getAuth();

const { error: collectionError, setDoc } = useCollection('users')

const router = useRouter()

if (!states.value.signup && !states.value.createAct) {
  router.push({ name: 'Login' })
}

const policyAuth = ref(false)

const fname = ref('')
const lname = ref('')
const phone = ref('')
const gender = ref('')
const email = ref('')
const profileImage = ref('')
const dob = ref('')
const status = ref('')

const personalDetails = ref(null)

const onPolicyAuthorized = (isAuthorized) => {
  policyAuth.value = isAuthorized
}

const onPersonalDetailsSubmitted = async (_personalDetails) => {
  personalDetails.value = _personalDetails

  fname.value = personalDetails.value.fname
  lname.value = personalDetails.value.lname
  phone.value = personalDetails.value.phone
  gender.value = personalDetails.value.gender
  dob.value = personalDetails.value.dob
  status.value = personalDetails.value.status
  profileImage.value = personalDetails.value.profileImage
  email.value = personalDetails.value.email

  await handleSubmit()
}

const handleSubmit = async () => {
  if (personalDetails.value) {
    try {
      flag.value = true

      const user_data = {
        fname: fname.value,
        lname: lname.value,
        phone: phone.value,
        gender: gender.value,
        email: email.value,
        dob: dob.value,
        status: status.value,
        profileImage: profileImage.value,
        uid: projectAuth.currentUser?.uid || null,
        createdAt: new Date(),
        points: 0
      }

      if (states.value.authPhoneNumber) {
        const res = await updateLogin({
          uid: user_data.uid,
          email: user_data.email,
          password: personalDetails.value.pwd
        })

        if (res.data.success) {
          await setDoc(user_data.uid, user_data)

          if (!collectionError._value) {
            states.value.user = user_data
            states.value.signup = false
            states.value.createAct = false
            showModal('החשבון נוצר בהצלחה', 'success')
            router.push({ name: "Home" })
          } else {
            states.value.user = null
            states.value.id = null
            states.value.signup = false
            showModal('ארעה שגיאה בשמירת פרטי החשבון', 'error')
          }
        } else {
          if (res.data.message == 'The email address is already in use by another account.') {
            showModal('האימייל שהוזן כבר בשימוש בחשבון אחר.', 'error')
          } else if (res.data.message == 'The user with the provided phone number already exists.') {
            showModal('הטלפון שהוזן כבר בשימוש בחשבון אחר.', 'error')
          } else if (res.data.message == 'The password must be a string with at least 6 characters.') {
            showModal('יש להזין סיסמה בת 6 תווים לפחות', 'error')
          } else {
            showModal('ארעה שגיאה ביצירת החשבון', 'error')
          }
        }
      } else {
        const res = await create_user_with_phone_email({
          email: user_data.email,
          password: personalDetails.value.pwd,
          phone: personalDetails.value.phone,
          user_data
        })

        if (res.data.success) {
          await signInWithEmailAndPassword(auth, user_data.email, personalDetails.value.pwd)
          states.value.createAct = false
          router.push({ name: "Home" })
        } else {
          if (res.data.message == 'The email address is already in use by another account.') {
            showModal('האימייל שהוזן כבר בשימוש בחשבון אחר.', 'error')
          } else if (res.data.message == 'The user with the provided phone number already exists.') {
            showModal('הטלפון שהוזן כבר בשימוש בחשבון אחר.', 'error')
          } else if (res.data.message == 'The password must be a string with at least 6 characters.') {
            showModal('יש להזין סיסמה בת 6 תווים לפחות', 'error')
          } else {
            showModal('ארעה שגיאה ביצירת החשבון', 'error')
          }
        }
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      flag.value = false
    }
  }
}
</script>

<style scoped>
/* PC Mode - Better visibility */
.signup-page {
  min-height: 100%;
  width: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
}

.signup-container {
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

/* Brand Section */
.brand-section {
  text-align: center;
  color: white;
}

.logo-wrapper {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.logo {
  width: 38px;
  height: 38px;
}

.app-name {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 4px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.app-tagline {
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
}

/* Signup Card */
.signup-card {
  width: 100%;
  background: white;
  border-radius: 20px;
  padding: 20px 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  direction: rtl;
  position: relative;
}

/* Step Indicator */
.step-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #e5e7eb;
}

.step {
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0.5;
  transition: all 0.3s ease;
}

.step.active {
  opacity: 1;
}

.step.completed {
  opacity: 0.7;
}

.step-number {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  transition: all 0.3s ease;
}

.step.active .step-number {
  background: #667eea;
  color: white;
}

.step.completed .step-number {
  background: #10b981;
  color: white;
}

.step-label {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
}

.step-line {
  width: 40px;
  height: 2px;
  background: #e5e7eb;
  transition: all 0.3s ease;
}

.step-line.completed {
  background: #10b981;
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-spinner {
  background: white;
  padding: 24px 32px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #667eea;
  font-weight: 500;
}

.spinner {
  width: 24px;
  height: 24px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Footer */
.footer-text {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin: 0;
}

.login-link {
  color: white;
  font-weight: 600;
  text-decoration: none;
}

.login-link:hover {
  text-decoration: underline;
}

/* Mobile Responsive - Full width form with proper scrolling */
@media (max-width: 480px) {
  .signup-page {
    padding: 6px;
    padding-top: 8px;
    height: 100%;
    overflow: hidden;
    align-items: stretch;
  }

  .signup-container {
    max-width: 100%;
    gap: 6px;
    height: 100%;
    overflow: hidden;
  }

  .brand-section {
    flex-shrink: 0;
  }

  .signup-card {
    padding: 12px 10px;
    border-radius: 14px;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    box-shadow: none !important;
  }

  .step-indicator {
    flex-shrink: 0;
    margin-bottom: 10px;
    padding-bottom: 10px;
  }

  .logo-wrapper {
    width: 44px;
    height: 44px;
    margin-bottom: 6px;
  }

  .logo {
    width: 28px;
    height: 28px;
  }

  .app-name {
    font-size: 17px;
  }

  .app-tagline {
    font-size: 12px;
  }

  .step-label {
    font-size: 12px;
  }

  .step-number {
    width: 20px;
    height: 20px;
    font-size: 11px;
  }

  .footer-text {
    flex-shrink: 0;
    padding-top: 6px;
  }
}

/* Low height phones - more compact header */
@media (max-height: 700px) and (max-width: 480px) {
  .signup-page {
    padding: 6px;
    padding-top: 6px;
  }

  .signup-container {
    gap: 6px;
  }

  .brand-section {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .logo-wrapper {
    width: 36px;
    height: 36px;
    margin: 0;
  }

  .logo {
    width: 24px;
    height: 24px;
  }

  .app-name {
    font-size: 16px;
    margin: 0;
  }

  .app-tagline {
    display: none;
  }

  .signup-card {
    padding: 10px 8px;
    flex: 1;
    min-height: 0;
    max-height: none;
  }

  .step-indicator {
    margin-bottom: 8px;
    padding-bottom: 8px;
    flex-shrink: 0;
  }

  .footer-text {
    flex-shrink: 0;
  }
}

/* Very low height phones */
@media (max-height: 600px) and (max-width: 480px) {
  .signup-page {
    padding: 6px;
    padding-top: 6px;
  }

  .signup-container {
    gap: 6px;
  }

  .brand-section {
    gap: 8px;
  }

  .logo-wrapper {
    width: 32px;
    height: 32px;
  }

  .logo {
    width: 20px;
    height: 20px;
  }

  .app-name {
    font-size: 14px;
  }

  .signup-card {
    padding: 8px 6px;
  }

  .step-indicator {
    margin-bottom: 6px;
    padding-bottom: 6px;
  }

  .step-number {
    width: 18px;
    height: 18px;
    font-size: 10px;
  }

  .step-label {
    font-size: 11px;
  }

  .step-line {
    width: 25px;
  }

  .footer-text {
    font-size: 11px;
  }
}
</style>
